import React from 'react';

/**
 * Shimmer block — uses CSS variables for dark/light theming.
 * Ultra-calm animation — subtle, not distracting.
 */
export const ShimmerBox: React.FC<{
    className?: string;
    delay?: string;
    style?: React.CSSProperties;
}> = ({ className = '', delay = '0s', style }) => (
    <div
        className={`relative overflow-hidden rounded-lg ${className}`}
        style={{ backgroundColor: 'var(--v2-skeleton-base)', ...style }}
    >
        <div
            className="absolute inset-0 -translate-x-full animate-shimmer"
            style={{
                backgroundImage: `linear-gradient(90deg, transparent 0%, var(--v2-skeleton-shimmer) 45%, var(--v2-skeleton-shimmer) 55%, transparent 100%)`,
                animationDelay: delay,
            }}
        />
    </div>
);

/**
 * Full-page skeleton — used as a loading state for entire route pages.
 * Matches: PageHeader + Card grid + Table layout
 */
const PageSkeleton: React.FC<{
    /** Number of stat cards to show */
    cards?: number;
    /** Whether to show a table skeleton below */
    showTable?: boolean;
    /** Number of table rows */
    tableRows?: number;
}> = ({ cards = 4, showTable = true, tableRows = 6 }) => (
    <div className="space-y-6 animate-fade-in-up">
        {/* Page Header skeleton */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
            <div className="space-y-2 flex-1">
                <ShimmerBox className="h-8 w-56 rounded-lg" delay="0s" />
                <ShimmerBox className="h-4 w-80 max-w-full rounded-md" delay="0.1s" />
            </div>
            <div className="flex items-center gap-2">
                <ShimmerBox className="h-10 w-28 rounded-lg" delay="0.15s" />
                <ShimmerBox className="h-10 w-10 rounded-lg" delay="0.2s" />
            </div>
        </div>

        {/* Stat cards row */}
        {cards > 0 && (
            <div className={`grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-${Math.min(cards, 4)}`}>
                {Array.from({ length: cards }).map((_, i) => (
                    <div
                        key={i}
                        className="rounded-xl border p-5"
                        style={{
                            background: 'var(--v2-bg-card)',
                            borderColor: 'var(--v2-border-card)',
                            boxShadow: 'var(--v2-shadow-card)',
                        }}
                    >
                        <div className="flex items-center gap-4">
                            <ShimmerBox className="h-12 w-12 rounded-xl flex-shrink-0" delay={`${i * 0.08}s`} />
                            <div className="flex-1 space-y-2">
                                <ShimmerBox className="h-4 w-24" delay={`${i * 0.08 + 0.05}s`} />
                                <ShimmerBox className="h-7 w-16 rounded-md" delay={`${i * 0.08 + 0.1}s`} />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        )}

        {/* Table skeleton */}
        {showTable && <TableSkeleton rows={tableRows} columns={5} />}
    </div>
);

/**
 * Generic table skeleton — matches the shared Table component layout.
 */
export const TableSkeleton: React.FC<{
    rows?: number;
    columns?: number;
}> = ({ rows = 6, columns = 5 }) => (
    <div
        className="overflow-hidden rounded-xl border"
        style={{
            background: 'var(--v2-bg-card)',
            borderColor: 'var(--v2-border-card)',
            boxShadow: 'var(--v2-shadow-card)',
        }}
    >
        {/* Header */}
        <div
            className="flex items-center gap-4 px-4 py-3 border-b"
            style={{ background: 'var(--v2-bg-surface)', borderColor: 'var(--v2-border-subtle)' }}
        >
            {Array.from({ length: columns }).map((_, i) => (
                <ShimmerBox
                    key={i}
                    className="h-4 rounded-md"
                    style={{ width: i === 0 ? '50px' : `${80 + Math.random() * 60}px` }}
                    delay={`${i * 0.04}s`}
                />
            ))}
        </div>
        {/* Rows */}
        {Array.from({ length: rows }).map((_, r) => (
            <div
                key={r}
                className="flex items-center gap-4 px-4 py-3.5 border-b last:border-0"
                style={{ borderColor: 'var(--v2-border-divider)' }}
            >
                {Array.from({ length: columns }).map((_, c) => (
                    <ShimmerBox
                        key={c}
                        className="h-4 rounded-md"
                        style={{
                            width: c === 0 ? '40px' :
                                c === 1 ? '140px' :
                                    `${70 + Math.random() * 50}px`,
                        }}
                        delay={`${r * 0.04}s`}
                    />
                ))}
            </div>
        ))}
        {/* Pagination */}
        <div
            className="flex items-center justify-between px-4 py-3 border-t"
            style={{ borderColor: 'var(--v2-border-subtle)', background: 'var(--v2-bg-surface)' }}
        >
            <ShimmerBox className="h-4 w-24 rounded-md" delay="0.1s" />
            <div className="flex gap-2">
                <ShimmerBox className="h-8 w-8 rounded-lg" />
                <ShimmerBox className="h-8 w-8 rounded-lg" delay="0.05s" />
            </div>
        </div>
    </div>
);

/**
 * Card skeleton — matches the Card component structure.
 */
export const CardSkeleton: React.FC<{
    lines?: number;
    showHeader?: boolean;
}> = ({ lines = 4, showHeader = true }) => (
    <div
        className="rounded-xl border overflow-hidden"
        style={{
            background: 'var(--v2-bg-card)',
            borderColor: 'var(--v2-border-card)',
            boxShadow: 'var(--v2-shadow-card)',
        }}
    >
        {showHeader && (
            <div
                className="px-6 pt-5 pb-3 border-b"
                style={{ borderColor: 'var(--v2-border-divider)', background: 'var(--v2-bg-card-inner)' }}
            >
                <ShimmerBox className="h-5 w-40 rounded-md" delay="0s" />
                <ShimmerBox className="h-3 w-56 rounded-md mt-2" delay="0.1s" />
            </div>
        )}
        <div className="px-6 py-5 space-y-3">
            {Array.from({ length: lines }).map((_, i) => (
                <ShimmerBox
                    key={i}
                    className="h-4 rounded-md"
                    style={{ width: `${60 + Math.random() * 35}%` }}
                    delay={`${i * 0.08}s`}
                />
            ))}
        </div>
    </div>
);

/**
 * Form skeleton — matches form layouts with labeled inputs.
 */
export const FormSkeleton: React.FC<{
    fields?: number;
    columns?: 1 | 2;
}> = ({ fields = 6, columns = 2 }) => (
    <div
        className="rounded-xl border p-6"
        style={{
            background: 'var(--v2-bg-card)',
            borderColor: 'var(--v2-border-card)',
            boxShadow: 'var(--v2-shadow-card)',
        }}
    >
        {/* Form title */}
        <ShimmerBox className="h-6 w-48 rounded-md mb-6" delay="0s" />

        <div className={`grid gap-5 grid-cols-1 ${columns === 2 ? 'md:grid-cols-2' : ''}`}>
            {Array.from({ length: fields }).map((_, i) => (
                <div key={i} className="space-y-2">
                    <ShimmerBox className="h-4 w-24 rounded-md" delay={`${i * 0.06}s`} />
                    <ShimmerBox className="h-11 w-full rounded-lg" delay={`${i * 0.06 + 0.03}s`} />
                </div>
            ))}
        </div>

        {/* Action buttons */}
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t" style={{ borderColor: 'var(--v2-border-divider)' }}>
            <ShimmerBox className="h-10 w-24 rounded-lg" delay="0.3s" />
            <ShimmerBox className="h-10 w-32 rounded-lg" delay="0.35s" />
        </div>
    </div>
);

export default PageSkeleton;
